import { FC, useEffect, useRef, useState } from 'react'

import StarIcon from '@/modules/chatting/icons/star.svg'
import { classNames } from '@/utils/helpers'

import { ScoringDropdown } from './ScoringDropdown'

const DROPDOWN_OFFSET_TOP = 90
const DROPDOWN_OFFSET_LEFT = 160
const HOVER_DELAY_MS = 150
const SCORING_OPTIONS_COUNT = 10

interface Props {
  onSelect: (score: number) => void
  selectedScore: number
}

export const ChatSuggestionScoring: FC<Props> = ({
  onSelect,
  selectedScore,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 })
  const iconRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const scoringOptions = Array.from(
    { length: SCORING_OPTIONS_COUNT },
    (_, index) => index + 1,
  )

  useEffect(() => {
    const updatePosition = () => {
      if (iconRef.current && isDropdownOpen) {
        const rect = iconRef.current.getBoundingClientRect()

        setDropdownPosition({
          top: rect.top + window.scrollY - DROPDOWN_OFFSET_TOP,
          left: rect.right + window.scrollX - DROPDOWN_OFFSET_LEFT,
        })
      }
    }

    if (isDropdownOpen) {
      updatePosition()
      window.addEventListener('scroll', updatePosition)
      window.addEventListener('resize', updatePosition)
    }

    return () => {
      window.removeEventListener('scroll', updatePosition)
      window.removeEventListener('resize', updatePosition)
    }
  }, [isDropdownOpen])

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setIsDropdownOpen(true)
  }

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsDropdownOpen(false)
    }, HOVER_DELAY_MS)
  }

  const handleScoreSelect = (score: number) => {
    onSelect(score)
    setIsDropdownOpen(false)
  }

  return (
    <div className="flex flex-row gap-2 p-4 border-l border-border">
      <div className="hidden xl:flex flex-row gap-2 items-center">
        <span className="text-sm font-medium">Poor</span>
        <div className="flex flex-row dark:bg-secondary-400 bg-secondary-100 gap-1 rounded-xl px-1 py-0.5">
          {scoringOptions.map((option) => (
            <span
              key={option}
              onClick={() => onSelect(option)}
              className={classNames(
                'text-sm font-medium px-1.5 rounded-xl hover:bg-primary-500 cursor-pointer hover:text-neutral-default',
                selectedScore === option &&
                  'bg-primary-500 text-neutral-default',
              )}
            >
              {option}
            </span>
          ))}
        </div>
        <span className="text-sm font-medium">Excellent</span>
      </div>

      <div className="xl:hidden relative">
        <div
          ref={iconRef}
          className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-400 cursor-pointer"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <StarIcon className="w-5 h-5 text-primary-500" />
          {selectedScore && (
            <span className="absolute -top-1 -right-1 bg-primary-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
              {selectedScore}
            </span>
          )}
        </div>

        <ScoringDropdown
          isOpen={isDropdownOpen}
          position={dropdownPosition}
          selectedScore={selectedScore}
          onSelect={handleScoreSelect}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
      </div>
    </div>
  )
}
